import { Injectable } from '@angular/core';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class KeyboardScrollService {

  constructor() { }

  public init() {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      this.reconcileStaleSystemInsets();
      this.registerKeyboardScrollHandler();
    } else if (platform === 'ios') {
      this.prewarmIOSKeyboard();
      this.reconcileStaleSystemInsets();
      Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => { });
      this.registerIOSKeyboardDismissHandler();
      this.registerKeyboardScrollHandler();
    }
  }

  private prewarmIOSKeyboard() {
    // Hack pour prévenir le lag massif du premier focus sous iOS 17+
    // Force WebKit à initialiser la session texte en arrière-plan pendant le splash screen
    const dummyInput = document.createElement('input');
    dummyInput.setAttribute('type', 'text');
    dummyInput.setAttribute('style', 'position: absolute; top: -1000px; left: -1000px; opacity: 0;');

    // Attendre que le DOM soit prêt
    setTimeout(() => {
      document.body.appendChild(dummyInput);
      dummyInput.focus();
      setTimeout(() => {
        dummyInput.blur();
        dummyInput.remove();
      }, 50);
    }, 1000);
  }

  private reconcileStaleSystemInsets() {
    // Forcer le recalcul des insets dès l'accès à la WebView (Cold Start)
    Keyboard.hide().catch(() => { });

    // Réitérer l'action de manière préventive lors du retour au premier plan
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        Keyboard.hide().catch(() => { });
      }
    });
  }

  private registerKeyboardScrollHandler() {
    const platform = Capacitor.getPlatform();

    // Écoute l'ouverture du clavier pour compenser le défilement uniquement si nécessaire
    Keyboard.addListener('keyboardDidShow', (info) => {
      // Sur iOS (ou plateformes sans adjustResize), on ajoute une marge compensatoire au body
      if (platform !== 'android') {
        document.body.style.paddingBottom = `${info.keyboardHeight}px`;
      }

      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.tagName !== 'BODY') {
          const rect = activeElement.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

          // Ne défiler que si l'élément actif est masqué par le clavier
          if (!isVisible) {
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 100);
    });

    // Supprime la marge de compensation lors de la fermeture du clavier
    Keyboard.addListener('keyboardDidHide', () => {
      if (platform !== 'android') {
        document.body.style.paddingBottom = '';
      }
    });
  }

  private registerIOSKeyboardDismissHandler() {
    let startY = 0;
    let startX = 0;
    let isScrolling = false;

    window.addEventListener('touchstart', (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      isScrolling = false;
    }, { passive: true });

    window.addEventListener('touchmove', (e: TouchEvent) => {
      const moveY = e.touches[0].clientY;
      const moveX = e.touches[0].clientX;
      if (Math.abs(moveY - startY) > 10 || Math.abs(moveX - startX) > 10) {
        isScrolling = true;
      }
    }, { passive: true });

    window.addEventListener('touchend', (e: TouchEvent) => {
      if (isScrolling) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        let target = e.target as HTMLElement | null;
        let isInput = false;

        while (target) {
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            isInput = true;
            break;
          }
          target = target.parentElement;
        }

        if (!isInput) {
          activeElement.blur();
        }
      }
    }, { passive: true });
  }
}

